// ─────────────────────────────────────────────────────────────────────────────
// FEATURE: Profile page  (route /profile — opened from the profile icon at the top right of the navbar)
// The account hub. Signed in: the customer's name, nickname, photo or initials, phone and email, an Edit profile button
// (→ /profile/edit, pages/EditProfile.tsx), three real counters (orders placed, saved addresses, favourite
// categories), then "Account essentials" (My Orders, Share & Earn with the customer's real referral code, Favourites,
// My Addresses, Help & Complaints), the site pages that used to sit in the top menu (About, Products, Compliance,
// Locations, Stories, Press, Contact), Policies, cookie & privacy preferences and Log out. Signed out (guest): a log-in
// card and the same site pages, so nothing that left the top menu becomes unreachable.
// Tiles: wallet balance, orders done (delivered), saved addresses. Download my data saves the account's profile, addresses, orders
// and wallet as one JSON file (built from the same APIs the site uses). Refund Assistance → /refund-assistance, Raise a Complaint → /complaint, Help Center → /help, Terms & Policies → /policies, Privacy & data → /account/privacy, FAQ → /faq.
// Deliberately NOT here (nothing supports it): eco-credits, "Harbor Club" membership, sub-zero promise,
// "₹100 gift" (referral rewards aren't issued yet — the row only shares the code), an automated refund flow, FAQ page, "cold-chain authenticated / formalin-free" footer claims.
// ─────────────────────────────────────────────────────────────────────────────
import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  Building2,
  Cookie,
  Fish,
  Gift,
  Heart,
  Info,
  CircleHelp,
  Hourglass,
  PartyPopper,
  Repeat,
  LifeBuoy,
  LogOut,
  MapPin,
  MapPinned,
  Wallet,
  Newspaper,
  Package,
  Pencil,
  ReceiptText,
  ShieldCheck,
  Smartphone,
  Star,
  UserRound,
  Accessibility,
  FileText,
  Leaf,
  Download,
  RotateCcw,
  TriangleAlert,
} from "lucide-react";
import Breadcrumbs from "../components/Breadcrumbs";
import PageTransition from "../components/PageTransition";
import { Row, Section } from "../components/account/AccountParts";
import useAuth from "../hooks/useAuth";
import useCookieConsent from "../hooks/useCookieConsent";
import useDocumentTitle from "../hooks/useDocumentTitle";
import { fetchAddresses } from "../utils/addresses";
import { downloadMyData } from "../utils/accountExport";
import { fetchPlans, isDue } from "../utils/plans";
import { fetchFavouriteCatches } from "../utils/favouriteCatches";
import { formatInr } from "../utils/priceFormat";
import { fetchOrders } from "../utils/orders";
import { fetchWallet } from "../utils/wallet";
import { fetchProfile, type Profile } from "../utils/profile";

const initials = (name: string | null) =>
  (name ?? "")
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join("") || "Z";

export default function Account() {
  useDocumentTitle("Profile");
  const auth = useAuth();
  const navigate = useNavigate();
  const { openPreferences } = useCookieConsent();
  const signedIn = auth.status === "signed-in";
  const [profile, setProfile] = useState<Profile | null>(null);
  const [counts, setCounts] = useState<{ orders: number; active: number; delivered: number; addresses: number } | null>(null);
  const [exporting, setExporting] = useState(false);
  const [balance, setBalance] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const saved = (useLocation().state as { saved?: boolean } | null)?.saved === true;
  const [savedCatches, setSavedCatches] = useState<number | null>(null);
  const [plans, setPlans] = useState<{ total: number; due: number } | null>(null);
  useEffect(() => {
    // Show the saved notice once: clear the router state so a reload doesn't repeat it.
    if (saved) window.history.replaceState({}, "");
  }, [saved]);

  useEffect(() => {
    if (!signedIn) return;
    let cancelled = false;
    fetchProfile().then((p) => !cancelled && setProfile(p), () => undefined);
    fetchWallet().then((w) => !cancelled && setBalance(w.balance), () => undefined);
    fetchPlans().then((rows) => !cancelled && setPlans({ total: rows.length, due: rows.filter((p) => isDue(p)).length }), () => undefined);
    fetchFavouriteCatches().then((rows) => !cancelled && setSavedCatches(rows.length), () => undefined);
    Promise.all([fetchOrders(), fetchAddresses()]).then(
      ([orders, addresses]) => !cancelled && setCounts({ orders: orders.length, active: orders.filter((o) => o.active).length, delivered: orders.filter((o) => o.status === "delivered").length, addresses: addresses.length }),
      () => undefined,
    );
    return () => {
      cancelled = true;
    };
  }, [signedIn]);

  // "Download my data": see utils/accountExport (the same file the Privacy & data page offers).
  const exportData = async () => {
    setExporting(true);
    try {
      await downloadMyData();
    } catch {
      setError("We couldn't prepare your data. Check your connection and try again.");
    } finally {
      setExporting(false);
    }
  };

  const logOut = async () => {
    await auth.signOut();
    navigate("/welcome", { replace: true });
  };

  return (
    <PageTransition>
      <div className="bg-foam-50 pb-24 pt-28 dark:bg-ocean-950 sm:pt-32">
        <Breadcrumbs />
        <div className="mx-auto flex max-w-xl flex-col gap-5 px-4 pt-2">
          <h1 className="sr-only">Profile</h1>

          {saved && <p role="status" className="rounded-xl bg-teal-100 px-4 py-2 text-sm font-semibold text-teal-700">Your profile was updated.</p>}

          {signedIn ? (
            <section aria-label="Your profile" className="rounded-2xl bg-white p-4 shadow-sm ring-1 ring-ocean-900/5 dark:bg-ocean-900 dark:ring-white/10">
              <div className="flex items-center gap-4">
                {profile?.photoDataUrl ? (
                  <img src={profile.photoDataUrl} alt="" className="h-16 w-16 shrink-0 rounded-full object-cover" />
                ) : (
                  <span aria-hidden="true" className="flex h-16 w-16 shrink-0 items-center justify-center rounded-full bg-ocean-800 font-display text-xl font-bold text-white">
                    {initials(profile?.fullName ?? null)}
                  </span>
                )}
                <div className="min-w-0">
                  <p className="truncate font-display text-2xl font-bold text-ocean-950 dark:text-foam-50">{profile?.fullName ?? "ZenFresh customer"}</p>
                  {profile?.nickname && <p className="text-sm text-ocean-800/80 dark:text-foam-100/75">“{profile.nickname}”</p>}
                  <p className="truncate text-sm text-ocean-800/80 dark:text-foam-100/75">{profile?.phone ?? auth.user?.phone}</p>
                  {profile?.email && <p className="truncate text-sm text-ocean-800/80 dark:text-foam-100/75">{profile.email}</p>}
                </div>
              </div>

              <Link to="/profile/edit" className="mt-4 inline-flex min-h-11 w-full items-center justify-center gap-2 rounded-full bg-ocean-700/10 px-5 text-sm font-bold text-ocean-900 hover:bg-ocean-700/15 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-teal-600 dark:bg-white/10 dark:text-foam-50">
                <Pencil className="h-4 w-4" aria-hidden="true" /> Edit profile
              </Link>

              <ul aria-label="Your account at a glance" className="mt-4 grid grid-cols-3 gap-2 text-center">
                {[
                  { label: "Wallet", value: balance === null ? "–" : formatInr(balance), to: "/wallet", icon: Wallet },
                  { label: "Orders done", value: counts ? String(counts.delivered) : "–", to: "/orders", icon: Package },
                  { label: "Saved addresses", value: counts ? String(counts.addresses) : "–", to: "/account/addresses", icon: MapPin },
                ].map((stat) => (
                  <li key={stat.label}>
                    <Link to={stat.to} className="flex h-full flex-col items-center gap-1 rounded-xl bg-ocean-700/5 px-2 py-3 hover:bg-ocean-700/10 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-teal-600 dark:bg-white/5">
                      <stat.icon className="h-5 w-5 text-teal-700 dark:text-teal-300" aria-hidden="true" />
                      <span className="font-display text-xl font-bold text-ocean-950 dark:text-foam-50">{stat.value}</span>
                      <span className="text-xs text-ocean-800/80 dark:text-foam-100/75">{stat.label}</span>
                    </Link>
                  </li>
                ))}
              </ul>
            </section>
          ) : (
            <section aria-label="Log in" className="rounded-2xl bg-white p-6 text-center shadow-sm ring-1 ring-ocean-900/5 dark:bg-ocean-900 dark:ring-white/10">
              <span aria-hidden="true" className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-teal-100 text-teal-700"><UserRound className="h-8 w-8" /></span>
              <p className="mt-3 font-display text-xl font-bold text-ocean-950 dark:text-foam-50">You&apos;re browsing as a guest</p>
              <p className="mt-1 text-sm text-ocean-800/80 dark:text-foam-100/75">Log in to see your orders, saved addresses and referral code.</p>
              <Link to="/welcome" className="mt-4 inline-flex min-h-11 items-center rounded-full bg-ocean-800 px-8 text-sm font-bold text-white hover:bg-ocean-700">Log in</Link>
            </section>
          )}

          {signedIn && (
            <Section title="Services & perks" hint="Account essentials">
              <Row icon={Wallet} title="Wallet" badge={balance === null ? undefined : formatInr(balance)} description="ZenFresh credits and your transaction history" to="/wallet" />
              <Row icon={Hourglass} title="Coming soon" badge="Preview" description="Screens we're building next — previews only" to="/coming-soon" />
              <Row icon={Repeat} title="Catch Pass" badge={plans === null ? undefined : plans.due > 0 ? `${plans.due} due` : `${plans.total} saved`} description="Your regular boxes, added to the cart in one tap" to="/catch-pass" />
              <Row icon={PartyPopper} title="Party & bulk planner" description="Plan a big order for a gathering" to="/party" />
              <Row icon={ReceiptText} title="My Orders" badge={counts ? `${counts.active} active` : undefined} description="Track active orders and re-order past ones" to="/orders" />
              <Row icon={Gift} title="Share & Earn" description={profile ? `Your code ${profile.referralCode} · see who joined` : "Invite friends and see who joined"} to="/share-earn" />
              <Row icon={Heart} title="Favourites" badge={savedCatches === null ? undefined : `${savedCatches} saved`} description="Catches you saved, with your cut and pack size" to="/favourites" />
              <Row icon={MapPin} title="My Addresses" badge={counts ? `${counts.addresses} saved` : undefined} description="Manage home, work and other delivery locations" to="/account/addresses" />
              <Row icon={RotateCcw} title="Refund Assistance" description="Cancel an order or report a problem with a delivery" to="/refund-assistance" />
              <Row icon={TriangleAlert} title="Raise a Complaint" description="A problem with a catch, its cut, packaging or delivery" to="/complaint" />
              <Row icon={LifeBuoy} title="Help Center" description="Answers, quick solutions and how to reach us" to="/help" />
              <Row icon={CircleHelp} title="FAQ" description="Questions & answers about orders, cuts, delivery and refunds" to="/faq" />
            </Section>
          )}

          <Section title="Explore ZenFresh" hint="From the menu bar">
            <Row icon={Info} title="About Us" description="How ZenFresh connects fishermen to your table" to="/about" />
            <Row icon={Info} title="About ZenFresh" description="Our mission, values and where we deliver, at a glance" to="/about-app" />
            <Row icon={Smartphone} title="Products & Services" description="Customer App, Driver App, Supplier Portal, Admin" to="/products" />
            <Row icon={ShieldCheck} title="Compliance" description="Food safety, certifications and standards" to="/compliance" />
            <Row icon={MapPinned} title="Locations" description="The coastal cities we deliver to, and a pincode check" to="/locations" />
            <Row icon={Star} title="Success Stories" description="Customers, fishermen and delivery partners" to="/success-stories" />
            <Row icon={Newspaper} title="Press & Investors" description="Coverage, media kit and investor information" to="/press-investors" />
            <Row icon={Building2} title="Contact Us" description="Get in touch with the ZenFresh team" to="/contact" />
          </Section>

          <Section title="Policies" hint="About">
            <Row icon={FileText} title="Terms & Policies" description="Cancellations, refunds and grievance contacts in short" to="/policies" />
            <Row icon={FileText} title="Terms & Conditions" description="The full terms of service" to="/terms" />
            <Row icon={ShieldCheck} title="Privacy Policy" description="What we collect and why" to="/privacy" />
            <Row icon={Leaf} title="Sustainability" description="Carbon impact, community welfare, fishing-ban transparency" to="/sustainability" />
            <Row icon={Package} title="Legal & Registrations" description="GSTIN, CIN and FSSAI registration details" to="/legal" />
            <Row icon={Accessibility} title="Accessibility" description="Our WCAG commitment and how to report barriers" to="/accessibility" />
          </Section>

          <Section title="Privacy & data" hint="Your choices">
            <Row icon={ShieldCheck} title="Privacy & data" description="Your choices, download or erase your data" to="/account/privacy" />
            <Row icon={Cookie} title="Cookie preferences" description="Choose which optional cookies we may use" onClick={openPreferences} />
            {signedIn && (
              <Row icon={Download} title="Download my data" badge={exporting ? "Preparing…" : "JSON"} description="Your profile, addresses, orders and wallet in one file" onClick={() => void exportData()} />
            )}
          </Section>

          {error && <p role="alert" className="text-center text-sm font-semibold text-coral-500 dark:text-coral-300">{error}</p>}

          {signedIn && (
            <button type="button" onClick={() => void logOut()} className="inline-flex min-h-12 items-center justify-center gap-2 rounded-2xl bg-coral-400/15 px-6 text-base font-bold text-coral-500 hover:bg-coral-400/25 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-coral-500 dark:text-coral-300">
              <LogOut className="h-5 w-5" aria-hidden="true" /> Log out of ZenFresh
            </button>
          )}

          <p className="flex items-center justify-center gap-1.5 text-center text-xs text-ocean-800/70 dark:text-foam-100/65">
            <Fish className="h-3.5 w-3.5" aria-hidden="true" /> ZenFresh — sustainably caught seafood, made for coastal freshness.
          </p>
        </div>
      </div>
    </PageTransition>
  );
}
